import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Switch,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../../globals/axios';
import { useAlert } from '../Alert';
import { Helmet } from 'react-helmet';
import CustomAppBar from '../AppBar';
import GenericButton from '../Custom/GenericButton';
import { useTranslation } from 'react-i18next';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[3],
}));

const Announcement = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [scheduleDate, setScheduleDate] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const handleSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('message', content);
    formData.append('send_to_all', sendToAll);
    if (!sendNow) {
      // Validate scheduleDate
      if (!scheduleDate || isNaN(Date.parse(scheduleDate))) {
        showAlert('error', 'Please select a valid schedule date and time.');
        setLoading(false);
        return;
      }
      formData.append('schedule_at', scheduleDate);
    }
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    try {
      await api.post(`/notifications/announcements/`, formData, { headers });
      showAlert('success', 'Announcement submitted successfully');
    } catch (error) {
      console.error('Error sending announcement:', error);
      showAlert('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Announcement - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Box sx={{ maxWidth: 900, margin: 'auto', p: 2 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <Stack alignItems="center">
              <CircularProgress color="primary" size={48} />
              <Typography sx={{ mt: 2 }}>
                {t('Sending announcement')}...
              </Typography>
            </Stack>
          </Box>
        )}
        {!loading && (
          <StyledPaper elevation={4}>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              color="primary"
            >
              {t('Send Announcement')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextField
                    label="Title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Content"
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                  />
                  <RadioGroup
                    row
                    value={sendToAll ? 'true' : 'false'}
                    onChange={e => setSendToAll(e.target.value === 'true')}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="All"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Farmers"
                    />
                  </RadioGroup>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">{t('Schedule')}</Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={sendNow}
                          onChange={() => setSendNow(!sendNow)}
                        />
                      }
                      label={sendNow ? 'Send Now' : 'Send Later'}
                    />
                  </FormGroup>
                  {!sendNow && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={['DateTimePicker']}>
                        <DateTimePicker
                          label="Schedule Date and Time"
                          onChange={value => {
                            if (value && value.isValid()) {
                              const selectedDate = value.toDate();
                              const now = new Date();
                              if (selectedDate > now) {
                                const isoString = selectedDate.toISOString();
                                setScheduleDate(isoString);
                              } else {
                                showAlert(
                                  'error',
                                  'Please select a future date and time.'
                                );
                                setScheduleDate('');
                              }
                            }
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  )}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                  >
                    <GenericButton
                      disabled={title.trim() === '' || content.trim() === ''}
                      backgroundColor="primary.main"
                      padding={'10px 20px'}
                      fontColor="#fff"
                      fontSize={'16px'}
                      onClick={handleSubmit}
                    >
                      {t('Submit')}
                    </GenericButton>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </StyledPaper>
        )}
      </Box>
    </>
  );
};

export default Announcement;
